export interface WaecTopicDetailMatch {
  topicUUID?: string;
  slug?: string;
  order?: number;
}

type WaecIdentifier = string | number | { _id?: string | number } | null | undefined;

function normalizeWaecIdentifier(identifier: WaecIdentifier): string {
  if (identifier && typeof identifier === 'object') {
    return normalizeWaecIdentifier(identifier._id);
  }
  return identifier == null ? '' : String(identifier);
}

export function waecIdentifiersMatch(left: WaecIdentifier, right: WaecIdentifier): boolean {
  const normalizedLeft = normalizeWaecIdentifier(left);
  return normalizedLeft !== '' && normalizedLeft === normalizeWaecIdentifier(right);
}

export function findWaecTopicDetail<T extends WaecTopicDetailMatch>(
  details: T[],
  topicUUID?: string,
  topicSlug?: string,
  detailSlug?: string,
  requireSlugMatch = false
): T | undefined {
  const targetSlug = detailSlug || topicSlug;
  const uuidMatch = topicUUID
    ? details.find((detail) =>
        waecIdentifiersMatch(detail.topicUUID, topicUUID)
      && (!requireSlugMatch || !targetSlug || !detail.slug || detail.slug === targetSlug)
      )
    : undefined;

  return uuidMatch || (targetSlug
    ? details.find((detail) => detail.slug === targetSlug)
    : undefined);
}

interface WaecTopicOrderable {
  uuid?: string;
  slug?: string;
  name?: string;
}

interface WaecOrderedDetail extends WaecTopicDetailMatch {
  order?: number;
}

export function sortWaecTopicsByAdminOrder<T extends WaecTopicOrderable>(
  topics: T[],
  details: WaecOrderedDetail[]
): T[] {
  const orderedDetails = details
    .map((detail, index) => ({ detail, index }))
    .sort((a, b) => {
      const orderDifference = Number(a.detail.order ?? 0) - Number(b.detail.order ?? 0);
      return orderDifference || a.index - b.index;
    })
    .map(({ detail }) => detail);

  return topics
    .map((topic, index) => ({
      topic,
      index,
      detail: findWaecTopicDetail(
        orderedDetails,
        topic.uuid,
        topic.slug
      ),
    }))
    .sort((a, b) => {
      if (a.detail && b.detail) {
        const orderDifference = Number(a.detail.order ?? 0) - Number(b.detail.order ?? 0);
        if (orderDifference) return orderDifference;
        const detailOrderDifference = orderedDetails.indexOf(a.detail) - orderedDetails.indexOf(b.detail);
        if (detailOrderDifference) return detailOrderDifference;
      } else if (a.detail || b.detail) {
        return a.detail ? -1 : 1;
      }

      return a.index - b.index;
    })
    .map(({ topic }) => topic);
}

interface WaecResource {
  title: string;
  url: string;
  type?: 'video' | 'article' | 'code';
}

interface WaecLessonContent<ResourceType, QuizType> {
  resources?: ResourceType[];
  quizzes?: QuizType[];
}

interface WaecDetailContent<ResourceType, QuizType> {
  resources?: ResourceType[];
  lessons?: WaecLessonContent<ResourceType, QuizType>[];
  quizzes?: QuizType[];
}

export function getWaecTopicResources<ResourceType extends WaecResource, QuizType>(
  detail: WaecDetailContent<ResourceType, QuizType> | null | undefined
): ResourceType[] {
  const allResources = [
    ...(detail?.resources || []),
    ...(detail?.lessons || []).flatMap((lesson) => lesson.resources || []),
  ];
  const seenUrls = new Set<string>();

  return allResources.filter((resource) => {
    const url = resource.url.trim();
    if (!resource.title.trim() || !url || seenUrls.has(url)) return false;
    seenUrls.add(url);
    return true;
  });
}

export function getWaecTopicQuizzes<QuizType, ResourceType>(
  detail: WaecDetailContent<ResourceType, QuizType> | null | undefined
): QuizType[] {
  if (detail?.quizzes?.length) return detail.quizzes;
  return (detail?.lessons || []).flatMap((lesson) => lesson.quizzes || []);
}
